// Deterministic client-copy hygiene shared by Stage 3, Stage 4 and Stage 5.
// Objective sentences belong in the quote Objective block, never as a priced
// scope inclusion. AI can refine work copy, but it cannot change that layout
// contract.

import { filterInclusionLines } from '../services/scope-subheadings';
import { classifySharedSiteLogisticsInclusion } from './scope-logistics-dedupe';

function textFromInclusion(value: unknown): string {
    if (typeof value === 'string') return value.trim();
    if (!value || typeof value !== 'object') return '';
    const row = value as Record<string, unknown>;
    for (const candidate of [row.text, row.label, row.description]) {
        if (typeof candidate === 'string' && candidate.trim()) return candidate.trim();
    }
    return '';
}

function normalise(value: unknown): string {
    return String(value || '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();
}

export function isObjectiveLikeScopeInclusion(value: unknown, scopeObjective = ''): boolean {
    const inclusion = textFromInclusion(value);
    if (!inclusion) return false;
    if (/^the objective of (?:this|the) quote is to\b/i.test(inclusion)) return true;
    const expected = normalise(scopeObjective);
    return Boolean(expected && normalise(inclusion) === expected);
}

export function stripObjectiveLikeScopeInclusions<T = Record<string, unknown>>(
    scopeItems: unknown,
    scopeObjective = '',
): T[] {
    if (!Array.isArray(scopeItems)) return [];
    return scopeItems.map((item) => {
        if (!item || typeof item !== 'object') return item as T;
        const row = item as Record<string, unknown>;
        if (!Array.isArray(row.inclusions)) return item as T;
        const inclusions = row.inclusions.filter(
            (entry) => !isObjectiveLikeScopeInclusion(entry, scopeObjective),
        );
        return inclusions.length === row.inclusions.length
            ? item as T
            : ({ ...row, inclusions } as T);
    });
}

// Work lines carry no closing full stop (house contract, PILLAR 3a). The
// closing line is the contract's default cleanup line word for word.
export const RJ_BIRD_PHYSICAL_SCOPE_OPENING = 'Supply and install temporary floor and surface protection to the immediate work area';
export const RJ_BIRD_PHYSICAL_SCOPE_CLOSING = 'Clean down the immediate work area and remove all work-associated rubbish at completion';

const PHYSICAL_WORK_RE = /\b(?:repair|replace|renew|rectif|remediat|rebuild|reinstate|install|make\s+good|seal|repoint|paint|excavat|waterproof|connect|construct|remove)\w*\b/i;
const NON_PHYSICAL_ROW_RE = /\b(?:preliminar|administration|admin\s+fee|hbcf|insurance|contract\s+preparation|planning\s+portal|design\s+report|diagnostic\s+report|investigation\s+only)\b/i;
// Site protection the opening line replaces: temporary protection, protecting
// the work area or its access, and drop sheets. The bookends delete a
// protection line, so this pattern is never wider than main's (f916b63a) and
// errs towards keeping a line:
// - temporary edge, fall, line or weather protection is priced work in its
//   own right (roof edge rails, tiger tails, a tarp over an open roof);
// - a line that does other work "to protect" something is that work ("Supply
//   and install cell board sheets to protect the waterproofed surfaces",
//   "Apply (2) coats of penetrating sealer to protect the paving surfaces").
// Correx and other protective sheet lines are not matched: a sheet line can be
// priced sheet-material work (anti-slip, rubber or vinyl sheet flooring,
// drainage cell, correx formwork), so it prints as the model wrote it.
const PROTECTION_BOOKEND_RE = /\b(?:supply\s+and\s+install\s+)?temporary\b(?:(?!\b(?:edge|fall|line|weather)\b).){0,40}\bprotection\b|(?<!\bto\s+)\bprotect\b.{0,60}\b(?:work\s+area|accessways?|floors?|finishes?|surfaces?)\b|\bdrop\s+sheets?\b/i;
const CLEAN_BOOKEND_RE = /\bclean\s+down\b.{0,100}\b(?:rubbish|waste|completion)\b|\bremove\b.{0,60}\bwork[- ]associated\s+(?:rubbish|waste)\b/i;

function isProtectionBookendLine(text: string): boolean {
    return PROTECTION_BOOKEND_RE.test(text);
}

/**
 * A site protection or cleanup line that ensureRjBirdPhysicalScopeBookends
 * replaces with the opening or closing line. Such a line does no diagnosed
 * repair, so the diagnosis delivery guard never counts it as work.
 */
export function isPhysicalScopeBookendLine(value: unknown): boolean {
    return physicalScopeBookendKind(value) !== '';
}

/** Which bookend a line is: 'protection' (opening), 'cleanup' (closing) or ''. */
export function physicalScopeBookendKind(value: unknown): 'protection' | 'cleanup' | '' {
    const text = textFromInclusion(value);
    if (!text) return '';
    if (isProtectionBookendLine(text)) return 'protection';
    return CLEAN_BOOKEND_RE.test(text) ? 'cleanup' : '';
}

function isBuilderEdited(row: Record<string, unknown>): boolean {
    const editedBy = String(row.editedBy || row.edited_by || '').trim().toLowerCase();
    return row.manuallyEdited === true
        || row.manualEdited === true
        || row.inclusionsEditedByUser === true
        || row.userEdited === true
        || Boolean(row.humanEditedAt || row.human_edited_at || row.inclusionsEditedAt || row.inclusions_edited_at)
        || ['user', 'user-edit', 'manual', 'operator', 'assistant', 'assistant-edit'].includes(editedBy);
}

function isPhysicalScopeRow(row: Record<string, unknown>): boolean {
    const text = [row.title, row.description, row.descriptionBrief, ...(Array.isArray(row.inclusions) ? row.inclusions.map(textFromInclusion) : [])]
        .map(value => String(value || '').trim())
        .filter(Boolean)
        .join(' ');
    return PHYSICAL_WORK_RE.test(text) && !NON_PHYSICAL_ROW_RE.test(String(row.title || ''));
}

const NOTE_LINE_RE = /^\s*note\b/i;
const IMAGES_LINE_RE = /^\s*refer\s+to\s+(?:the\s+)?images?\b/i;

function isHeadingEntry(value: unknown): boolean {
    return Boolean(value && typeof value === 'object' && (value as Record<string, unknown>).type === 'heading');
}

// The opening block the contract puts before the protection line: access,
// owner and loss-of-service NOTEs, then the images line.
function isOpeningBlockLine(value: unknown): boolean {
    if (isHeadingEntry(value)) return false;
    const text = textFromInclusion(value);
    return NOTE_LINE_RE.test(text) || IMAGES_LINE_RE.test(text);
}

// What may follow the cleanup line: NOTE lines and the removal of temporary
// barricades (the contract's step p, the structure check's rule C4).
const BARRICADE_REMOVAL_RE = /^\s*remove\s+(?:the\s+)?(?:temporary\s+)?(?:barricades?|barriers?|hoarding)\b/i;

function mayFollowCleanup(value: unknown): boolean {
    if (isHeadingEntry(value)) return false;
    const text = textFromInclusion(value);
    return NOTE_LINE_RE.test(text) || BARRICADE_REMOVAL_RE.test(text);
}

// The shared-site de-dupe (dedupeSharedSiteLogistics) reads the line the same
// way, so a quote with several items on one site still prints it once.
function sharedSiteReads(value: unknown, kind: 'protection' | 'cleanup'): boolean {
    const shared = classifySharedSiteLogisticsInclusion(
        value as Parameters<typeof classifySharedSiteLogisticsInclusion>[0],
    );
    return kind === 'protection' ? shared === 'siteProtection' : shared === 'cleanup';
}

/**
 * Gives an unedited physical row ONE protection line and ONE cleanup line,
 * where the house contract and RJ Bird's sent quotes put them: the protection
 * line as the first work step, after the opening NOTE lines and the images
 * line; the cleanup line after the work, followed only by NOTE lines or a
 * barricade removal. Every line the bookend patterns recognise is taken out,
 * as before, and one of each goes back: the AI's own line when the shared-site
 * de-dupe reads it the same way (the first protection line, the last cleanup
 * line), otherwise the generic line. The AI's line stays where it was when it
 * was already in its place.
 *
 * Until F44 (live 2026-10-12) the generic pair always printed, first and last:
 * the protection line above the access NOTEs, a specific line ("temporary
 * protection to the Unit 22 entry and terrace (floors and finishes)") replaced
 * by the generic one, and the cleanup line below the warranty and exclusion
 * NOTEs.
 */
export function ensureRjBirdPhysicalScopeBookends<T = Record<string, unknown>>(scopeItems: unknown): T[] {
    if (!Array.isArray(scopeItems)) return [];
    return scopeItems.map(item => {
        if (!item || typeof item !== 'object') return item as T;
        const row = item as Record<string, unknown>;
        if (!Array.isArray(row.inclusions) || isBuilderEdited(row) || !isPhysicalScopeRow(row)) return item as T;
        const lines = row.inclusions as unknown[];
        const kindOf = (entry: unknown) => (isHeadingEntry(entry) ? '' : physicalScopeBookendKind(entry));
        let protectionIndex = -1;
        let cleanupIndex = -1;
        lines.forEach((entry, index) => {
            const kind = kindOf(entry);
            if (kind === 'protection' && protectionIndex < 0 && sharedSiteReads(entry, 'protection')) protectionIndex = index;
            if (kind === 'cleanup' && sharedSiteReads(entry, 'cleanup')) cleanupIndex = index;
        });
        const protectionInPlace = protectionIndex >= 0
            && lines.slice(0, protectionIndex).every(isOpeningBlockLine);
        // A cleanup line that would be the only line under a subheading is not
        // in its place: it closes the item, not that group.
        const lineBeforeCleanup = lines.slice(0, Math.max(cleanupIndex, 0)).filter(entry => !kindOf(entry)).pop();
        const cleanupInPlace = cleanupIndex >= 0
            && lines.slice(cleanupIndex + 1).every(mayFollowCleanup)
            && !isHeadingEntry(lineBeforeCleanup);
        // The AI line that stays in place is marked so the pass below keeps it.
        const KEEP_PROTECTION = { keep: 'protection' };
        const KEEP_CLEANUP = { keep: 'cleanup' };
        const marked = lines.map((entry, index) => (
            index === protectionIndex && protectionInPlace ? KEEP_PROTECTION
                : index === cleanupIndex && cleanupInPlace ? KEEP_CLEANUP
                    : entry
        ));
        // A subheading names a group of lines; it is never a bookend line. A
        // set-up subheading whose only lines were protection lines goes with
        // them: the protection line above the first subheading stands for it.
        const inclusions: unknown[] = filterInclusionLines(
            marked,
            entry => entry === KEEP_PROTECTION || entry === KEEP_CLEANUP || !kindOf(entry),
        ).map(entry => (
            entry === KEEP_PROTECTION ? lines[protectionIndex]
                : entry === KEEP_CLEANUP ? lines[cleanupIndex]
                    : entry
        ));
        if (!cleanupInPlace) {
            let at = inclusions.length;
            while (at > 0 && mayFollowCleanup(inclusions[at - 1])) at -= 1;
            inclusions.splice(at, 0, cleanupIndex >= 0 ? lines[cleanupIndex] : RJ_BIRD_PHYSICAL_SCOPE_CLOSING);
        }
        if (!protectionInPlace) {
            let at = 0;
            while (at < inclusions.length && isOpeningBlockLine(inclusions[at])) at += 1;
            inclusions.splice(at, 0, protectionIndex >= 0 ? lines[protectionIndex] : RJ_BIRD_PHYSICAL_SCOPE_OPENING);
        }
        return { ...row, inclusions } as T;
    });
}

export default {
    ensureRjBirdPhysicalScopeBookends,
    isObjectiveLikeScopeInclusion,
    stripObjectiveLikeScopeInclusions,
};
